import { useCallback } from "react";

/**
 * Custom hook to manage stock and index trading handlers
 * Handles purchase and sell logic for stocks and indexes, including action card interactions
 */
export const useTradeHandlers = (
  socket,
  room,
  name,
  actionsRemaining,
  selectedActionCard,
  actionMode,
  executeActionCard,
  { pendingTransferSource, setPendingTransferSource, setActionMode, setSelectedActionCard, stockOwnershipByPlayer = {}, addNotification } = {}
) => {
  const handlePurchaseStock = useCallback((stock) => {
    // Check if we're in action card mode
    if (selectedActionCard) {
      if (selectedActionCard.actionType === "transfer_manipulators") {
        const applied = stock.appliedActionCards || [];
        const hasHalt = applied.some(c => c.actionType === "trading_halt");
        const hasDelayed = applied.some(c => c.actionType === "delayed_manipulate" && c.processed !== true);
        if (actionMode === "selecting_transfer_source") {
          if (hasHalt) {
            return;
          }
          if (!hasDelayed) {
            return;
          }
          setPendingTransferSource?.(stock);
          setActionMode?.("selecting_transfer_target");
          addNotification?.("Now select target stock (must be owned by another player; cannot be under Trading Halt)", null);
          return;
        }
        if (actionMode === "selecting_transfer_target" && pendingTransferSource && stock.name !== pendingTransferSource.name) {
          if (hasHalt) {
            return;
          }
          const ownership = stockOwnershipByPlayer[stock.name] || {};
          const otherOwners = Object.keys(ownership).filter(p => p !== name);
          if (otherOwners.length === 0) {
            return;
          }
          executeActionCard(selectedActionCard, {
            sourceStockName: pendingTransferSource.name,
            targetStockName: stock.name
          });
          setPendingTransferSource?.(null);
          setActionMode?.(null);
          setSelectedActionCard?.(null);
          return;
        }
        return;
      }
      if ((selectedActionCard.actionType === "manipulate" || selectedActionCard.actionType === "delayed_manipulate") && actionMode === "selecting_stock_up") {
        executeActionCard(selectedActionCard, stock, "up");
        return;
      } else if (selectedActionCard.actionType === "remove_manipulators" && actionMode === "selecting_stock_remove") {
        executeActionCard(selectedActionCard, stock);
        return;
      } else if (selectedActionCard.actionType === "trading_halt" && actionMode === "selecting_stock_ban") {
        executeActionCard(selectedActionCard, stock);
        return;
      } else if (selectedActionCard.actionType === "rug_pull" && actionMode === "selecting_rug_pull") {
        executeActionCard(selectedActionCard, stock);
        return;
      } else if (selectedActionCard.actionType === "double_down" && actionMode === "selecting_double_down") {
        executeActionCard(selectedActionCard, stock);
        setActionMode?.(null);
        setSelectedActionCard?.(null);
        return;
      }
    }
    
    // Normal purchase logic
    const myActions = actionsRemaining[name] || 0;
    if (myActions <= 0) {
      return;
    }

    socket.current.emit("purchase_stock", {
      room: room,
      playerName: name,
      stock: stock
    });
  }, [socket, room, name, actionsRemaining, selectedActionCard, actionMode, executeActionCard, pendingTransferSource, setPendingTransferSource, setActionMode, setSelectedActionCard, stockOwnershipByPlayer, addNotification]);

  const handleSellStock = useCallback((stock) => {
    // Check if we're in action card mode (spread rumor or delayed manipulator)
    if (selectedActionCard && (selectedActionCard.actionType === "manipulate" || selectedActionCard.actionType === "delayed_manipulate") && actionMode === "selecting_stock_down") {
      executeActionCard(selectedActionCard, stock, "down");
      return;
    }
    
    // Normal sell logic - only proceed if we're not in action mode
    if (!selectedActionCard) {
      socket.current.emit("sell_stock", {
        room: room,
        playerName: name,
        stock: stock
      });
    }
  }, [socket, room, name, selectedActionCard, actionMode, executeActionCard]);

  const handlePurchaseIndex = useCallback((index) => {
    // Check if we're in action card mode (rug pull)
    if (selectedActionCard && selectedActionCard.actionType === "rug_pull" && actionMode === "selecting_rug_pull") {
      executeActionCard(selectedActionCard, index);
      return;
    }
    
    const myActions = actionsRemaining[name] || 0;
    if (myActions <= 0) {
      return;
    }

    socket.current.emit("purchase_index", {
      room: room,
      playerName: name,
      index: index
    });
  }, [socket, room, name, actionsRemaining, selectedActionCard, actionMode, executeActionCard]);

  const handleSellIndex = useCallback((index) => {
    // Check if we're in action card mode (rug pull)
    if (selectedActionCard && selectedActionCard.actionType === "rug_pull" && actionMode === "selecting_rug_pull") {
      executeActionCard(selectedActionCard, index);
      return;
    }
    
    const myActions = actionsRemaining[name] || 0;
    if (myActions <= 0) {
      return;
    }

    socket.current.emit("sell_index", {
      room: room,
      playerName: name,
      index: index
    });
  }, [socket, room, name, actionsRemaining, selectedActionCard, actionMode, executeActionCard]);

  return {
    handlePurchaseStock,
    handleSellStock,
    handlePurchaseIndex,
    handleSellIndex
  };
};

